import { formatDateForTitle, type Locale } from "@/i18n";
import { edgeRead } from "@/lib/api/_fetch";

/**
 * Engine-backed daily horoscope source for the /rashifal/ and
 * /hi/aaj-ka-rashifal/ hubs, /today-horoscope/ and /tomorrow-horoscope/.
 * It lived under the /daily-horoscope/ route until that page merged into the
 * /rashifal/ hub on 2026-10-26 (owner decision).
 *
 * The backend computes every value (`GET /rashifal/engine/daily`): the IST
 * civil date, tomorrow's date, the Moon's rashi and nakshatra, and each rashi's
 * transit picture. This module only fetches, validates and formats — it never
 * derives a date from the server clock and never invents a forecast line.
 * When the engine is unreachable it returns null and the
 * pages render without a date, a forecast, or an "Updates Daily" claim.
 */

export type EngineDay = "today" | "tomorrow";
export type EngineLanguage = "en" | "hi";
export type DailyHoroscopeSurface =
  | "daily_horoscope"
  | "today_horoscope"
  | "tomorrow_horoscope"
  | "rashifal_hub"
  | "aaj_ka_rashifal_hub"
  | "rashifal_sign"
  | "hi_rashifal_sign";

export interface EngineRashiPicture {
  rashi: string;
  rashi_name_en: string;
  rashi_name_hi: string;
  moon_house: number;
  content: string;
}

export interface EngineDailyHoroscope {
  day: EngineDay;
  /** Edition date the pictures were computed for (IST civil date). */
  date: string;
  /** The IST civil date at render time, as the engine resolved it. */
  today: string;
  language: EngineLanguage;
  source: "engine";
  moon_rashi: string;
  moon_rashi_name: string;
  moon_nakshatra: string;
  rashis: EngineRashiPicture[];
}

const RASHI_COUNT = 12;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const RETIRED_PLACEHOLDER = /temporarily unavailable/i;
const ENGINE_TIMEOUT_MS = 8_000;

function reportDailyHoroscopeSsrDegradation(
  surface: DailyHoroscopeSurface,
  day: EngineDay,
  reason: string
): void {
  // Cloudflare Worker logs are the governed server/edge sink (see
  // panchang.server.ts). One stable JSON line, no seeker or request value.
  console.error(
    JSON.stringify({
      severity: "ERROR",
      event: "daily_horoscope_ssr_degraded",
      reason,
      surface,
      day,
    })
  );
}

function hasEngineDates(snapshot: Partial<EngineDailyHoroscope>): boolean {
  return ISO_DATE.test(String(snapshot.date)) && ISO_DATE.test(String(snapshot.today));
}

function hasRenderableContent(rashi: Partial<EngineRashiPicture> | null | undefined): boolean {
  const content = String(rashi?.content ?? "").trim();
  return content.length > 0 && !RETIRED_PLACEHOLDER.test(content);
}

export function assertEngineDailyHoroscope(value: unknown): EngineDailyHoroscope {
  const snapshot = value as Partial<EngineDailyHoroscope> | null;
  if (!snapshot || typeof snapshot !== "object") throw new Error("engine_snapshot_missing");
  if (snapshot.source !== "engine") throw new Error("engine_snapshot_source_mismatch");
  if (!hasEngineDates(snapshot)) throw new Error("engine_snapshot_date_missing");
  const rashis = Array.isArray(snapshot.rashis) ? snapshot.rashis : [];
  if (rashis.length !== RASHI_COUNT) throw new Error("engine_snapshot_incomplete");
  if (!rashis.every(hasRenderableContent)) throw new Error("engine_snapshot_placeholder_content");
  return snapshot as EngineDailyHoroscope;
}

export async function getDailyEngineHoroscope(
  day: EngineDay,
  surface: DailyHoroscopeSurface,
  language: EngineLanguage = "en"
): Promise<EngineDailyHoroscope | null> {
  try {
    const raw = await edgeRead<unknown>(
      "rashifal/engine/daily",
      `language=${language}&day=${day}`,
      ENGINE_TIMEOUT_MS
    );
    return assertEngineDailyHoroscope(raw);
  } catch (error) {
    const reason =
      error instanceof Error && error.message.startsWith("engine_snapshot_")
        ? error.message
        : "engine_snapshot_unavailable";
    reportDailyHoroscopeSsrDegradation(surface, day, reason);
    return null;
  }
}

/** Human dateline for an engine ISO date, rendered in IST like the hubs' dateline. */
export function formatEngineDate(isoDate: string, locale: Locale = "en"): string {
  // Noon IST keeps the civil day stable under any host timezone.
  return formatDateForTitle(locale, new Date(`${isoDate}T12:00:00+05:30`));
}
